const API_BASE = process.env.REACT_APP_API_URL || '/api';

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;
  const res = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  const text = await res.text();
  let data: T;
  try {
    data = text ? JSON.parse(text) : ({} as T);
  } catch {
    throw new Error(text || 'Greška');
  }
  if (!res.ok) {
    throw new Error((data as { error?: string }).error || `HTTP ${res.status}`);
  }
  return data;
}

export const api = {
  auth: {
    login: (username: string, password: string) =>
      fetchApi<{ success: boolean; user: User }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      }),
    logout: () => fetchApi<{ success: boolean }>('/auth/logout', { method: 'POST' }),
    me: () => fetchApi<{ user: User }>('/auth/me'),
    csrf: () => fetchApi<{ csrf_token: string }>('/auth/csrf'),
  },
  narudzbenice: {
    list: (params?: { filter_godina?: number; filter_odjel?: number }) => {
      const q = new URLSearchParams(params as Record<string, string>).toString();
      return fetchApi<NarudzbeniceListResponse>(`/narudzbenice?action=list&${q}`);
    },
    get: (id: number) => fetchApi<{ success: boolean; narudzbenica: Narudzbenica; stavke: Stavka[] }>(`/narudzbenice?action=get&id=${id}`),
    view: (id: number) => fetchApi<{ success: boolean; narudzbenica: Narudzbenica; stavke: Stavka[] }>(`/narudzbenice?action=view&id=${id}`),
    copy: (id: number) => fetchApi<{ success: boolean; narudzbenica: Narudzbenica; stavke: Stavka[] }>(`/narudzbenice?action=copy&id=${id}`),
    getNextBroj: (godina?: number) =>
      fetchApi<{ broj: string }>(`/narudzbenice?action=get_next_broj&godina=${godina || new Date().getFullYear()}`),
    save: (data: NarudzbenicaFormData) =>
      fetchApi<{ success: boolean; id: number; godina: number }>('/narudzbenice', {
        method: 'POST',
        body: JSON.stringify({ ...data, action: 'save' }),
      }),
    delete: (id: number, csrf: string) =>
      fetchApi<{ success: boolean; godina: number }>('/narudzbenice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `action=delete&id=${id}&csrf_token=${csrf}`,
      }),
    storno: (id: number, napomena: string, csrf: string) =>
      fetchApi<{ success: boolean; godina: number }>('/narudzbenice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `action=storno&id=${id}&storno_napomena=${encodeURIComponent(napomena)}&csrf_token=${csrf}`,
      }),
    reaktiviraj: (id: number, csrf: string) =>
      fetchApi<{ success: boolean; godina: number }>('/narudzbenice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `action=reaktiviraj&id=${id}&csrf_token=${csrf}`,
      }),
  },
  firme: {
    get: (id: number) => fetchApi<FirmaResponse>(`/firme/${id}`),
    list: (params?: { filter?: string; search?: string }) => {
      const q = new URLSearchParams(params as Record<string, string>).toString();
      return fetchApi<{ firme: Firma[] }>(`/firme?${q}`);
    },
  },
  email: {
    send: (narudzbenicaId: number, recipientEmail: string, message?: string) =>
      fetchApi<{ success: boolean; message: string }>('/email', {
        method: 'POST',
        body: JSON.stringify({
          narudzbenica_id: narudzbenicaId,
          recipient_email: recipientEmail,
          message: message || 'U prilogu se nalazi narudžbenica.',
        }),
      }),
  },
};

export function getIspisUrl(id: number): string {
  return `${API_BASE}/ispis?id=${id}`;
}

export async function openIspis(id: number): Promise<void> {
  const url = getIspisUrl(id);
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error('Greška pri učitavanju ispisa');
  const html = await res.text();
  const w = window.open('', '_blank');
  if (w) {
    w.document.write(html);
    w.document.close();
  } else {
    throw new Error('Dopustite otvaranje pop-up prozora');
  }
}

export function getPriloziUrl(narudzbenicaId: number): string {
  const base = (process.env.REACT_APP_API_URL || '/api').replace(/\/api\/?$/, '');
  return `${base || ''}/legacy/prilozi.php?narudzbenica_id=${narudzbenicaId}`;
}

export interface User {
  id: number;
  username: string;
  ime: string;
  prezime: string;
  role: 'korisnik' | 'administrator' | 'promatrac';
  upravni_odjel_id?: number;
  odjel_sifra?: string;
  odjel_naziv?: string;
}

export interface Narudzbenica {
  id: number;
  broj_narudzbenice: string;
  tip_nabave: string;
  datum: string;
  kupac_naziv: string;
  isporucitelj_naziv: string;
  ukupno_s_pdv: number;
  status: string;
  stornirana: number;
  storno_napomena?: string;
  storno_datum?: string;
  odjel_sifra?: string;
  sastavio_ime?: string;
  kupac_adresa?: string;
  isporucitelj_adresa?: string;
  nacin_otpreme?: string;
  rok_isporuke?: string;
  nacin_placanja?: string;
  platiti_u_roku?: string;
  ukupno_bez_pdv?: number;
  pdv_iznos?: number;
  [key: string]: unknown;
}

export interface Stavka {
  id?: number;
  naziv_dobra_usluge: string;
  jed_mjere: string;
  kolicina: number;
  cijena_bez_pdv: number;
  rabat_postotak: number;
  pdv_stopa: number;
  iznos: number;
}

export interface NarudzbeniceListResponse {
  narudzbenice: Narudzbenica[];
  dostupne_godine: number[];
  zadnja_id_za_godinu: number | null;
  odjeli_filter: { id: number; sifra: string; naziv: string }[];
  kupci: { id: number; naziv: string }[];
  isporucitelji: { id: number; naziv: string }[];
  defaults: { default_kupac_id?: string; default_isporucitelj_id?: string };
}

export interface NarudzbenicaFormData {
  action: string;
  edit_id?: number;
  csrf_token: string;
  broj_narudzbenice: string;
  tip_nabave: string;
  broj_ponude?: string;
  datum_ponude?: string;
  nas_znak?: string;
  nas_broj?: string;
  datum: string;
  kupac_naziv: string;
  kupac_adresa: string;
  kupac_oib: string;
  kupac_iban?: string;
  isporucitelj_naziv: string;
  isporucitelj_adresa: string;
  isporucitelj_oib: string;
  nacin_otpreme?: string;
  nacin_otpreme_tip?: string;
  rok_isporuke?: string;
  nacin_placanja?: string;
  platiti_u_roku?: string;
  sastavio_ime?: string;
  stavke_naziv: string[];
  stavke_jm: string[];
  stavke_kolicina: string[];
  stavke_cijena: string[];
  stavke_rabat: string[];
  stavke_pdv: string[];
}

export interface Firma {
  id: number;
  naziv: string;
  adresa: string;
  oib: string;
  iban?: string;
}

export interface FirmaResponse {
  success: boolean;
  naziv: string;
  adresa: string;
  oib: string;
  iban?: string;
}
